import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';

import { Autocomplete } from 'components/FormFields';
import { PricingActions } from 'state/modules/pricing';
import { truncateKeywordSpaces } from 'utils/utils';

const AdHocSelect = ({
  label,
  variant,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  customParams,
  type,
  to,
  disabled,
  accountLevel,
  margin,
}) => {
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    status: 'ACTIVE',
    ...customParams,
  });

  useEffect(() => {
    dispatch(PricingActions.resetAdhocTypes());
  }, []);

  const { adhocs, fetchingAdhocs } = useSelector((state) => state.pricing);

  const fetchAdHocsTypes = useCallback(
    debounce((params) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(PricingActions.fetchAdHocTypes(newParams));
    }, 1000),
    []
  );
  useEffect(() => {
    dispatch(PricingActions.resetAdhocTypes());
  }, []);

  const filterFee = (options) => {
    return options.filter((item) => item.accounting_type.is_revenue && type.active);
  };

  const filterCredits = (options) => {
    return options.filter(
      (item) =>
        !item.accounting_type.is_revenue && !item.accounting_type.is_account_level && item.active
    );
  };

  const filterAccountCredit = (options) => {
    return options.filter(
      (item) =>
        item.accounting_type.is_account_level && !item.accounting_type.is_revenue && item.active
    );
  };

  const filteredOptions = (options) => {
    let data = [];

    if (to === 'driver' || to === 'Organization') {
      data = filterAccountCredit(options);
      return data;
    }

    if (type === 'credit') {
      data = filterCredits(options);
    } else {
      data = filterFee(options);
    }

    return data;
  };

  useEffect(() => {
    if (!value) return;
    fetchAdHocsTypes(params);
  }, []) // eslint-disable-line

  useEffect(() => {
    if (value || value === '' || !defaultValue) {
      const defaultVal = adhocs.find((el) => el.id === parseInt(value));
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else if (value === '') {
        setDefaultValue(null);
      }
    }
  }, [value, adhocs]) // eslint-disable-line

  const handleFetchAdHocsTypes = () => {
    if (filteredOptions(adhocs).length === 0 && !fetchingAdhocs && !params?.keyword)
      dispatch(PricingActions.fetchAdHocTypes(params));
  };

  return (
    <Autocomplete
      label={label}
      margin={margin}
      fullwidth="true"
      variant={variant}
      disabled={disabled}
      value={defaultValue}
      id="user-groups-select"
      loading={fetchingAdhocs}
      onHighlightChange={handleFetchAdHocsTypes}
      options={filteredOptions(adhocs)}
      getOptionLabel={(option) => option.name || ''}
      onChange={(ev, val) => setFieldValue(name, val?.id || '')}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option.id === value.id}
      onInputChange={(ev, val) => fetchAdHocsTypes({ ...params, keyword: val })}
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
    />
  );
};

AdHocSelect.propTypes = {
  label: PropTypes.string,
  margin: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

AdHocSelect.defaultProps = {
  required: true,
  disabled: false,
  margin: 'normal',
  form: {
    errors: {},
    touched: {},
  },
  customParams: {},
  variant: 'standard',
  label: 'Select...',
};

export default AdHocSelect;
